import React, { useCallback, useEffect, useState } from "react";
import {
  RefreshCw,
  Search,
  ScrollText,
  Shield,
  FileDown,
} from "lucide-react";
import { Card, Button, Input, Table, Pagination, Badge, Tooltip } from "../../components/common/UI";
import { apiFetch, useApp } from "../../context/AppContext";

function csvCell(value) {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function formatWhen(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return value;
  }
}

function roleLabel(role) {
  if (role === "tenant_admin") return "Tenant Admin";
  if (role === "user") return "User";
  return role || "—";
}

function methodBadge(method) {
  const variants = {
    GET: "primary",
    POST: "success",
    PUT: "warning",
    PATCH: "warning",
    DELETE: "danger",
  };
  return <Badge variant={variants[method] || "default"}>{method}</Badge>;
}

function statusBadge(code) {
  if (code >= 200 && code < 400) return <Badge variant="success">{code}</Badge>;
  if (code >= 400 && code < 500) return <Badge variant="warning">{code}</Badge>;
  return <Badge variant="danger">{code}</Badge>;
}

function buildCsv(rows) {
  const headers = [
    "When",
    "Actor",
    "Email",
    "Role",
    "Action",
    "Module",
    "Method",
    "Status",
    "Duration (ms)",
  ];
  const lines = [headers.map(csvCell).join(",")];
  for (const row of rows) {
    lines.push(
      [
        formatWhen(row.created_at),
        row.actor_name || "",
        row.actor_email || "",
        roleLabel(row.actor_role),
        row.action,
        row.resource,
        row.method,
        row.status_code,
        row.duration_ms ?? "",
      ]
        .map(csvCell)
        .join(",")
    );
  }
  return lines.join("\r\n");
}

function downloadFile(content, filename, mimeType) {
  const blob = new Blob(["\uFEFF" + content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function AuditLogsPage() {
  const { showNotification } = useApp();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [method, setMethod] = useState("");
  const [role, setRole] = useState("");
  const [statusGroup, setStatusGroup] = useState("");
  const pageSize = 20;

  const fetchLogs = useCallback(
    async (targetPage = 1) => {
      setLoading(true);
      try {
        const query = new URLSearchParams({
          page: String(targetPage),
          page_size: String(pageSize),
        });
        if (search) query.append("search", search);
        if (method) query.append("method", method);
        if (role) query.append("role", role);
        if (statusGroup) query.append("status_group", statusGroup);
        const res = await apiFetch(`/tenant/audit-logs?${query.toString()}`, {
          cache: "no-store",
        });
        if (!res.ok) throw new Error("Failed to fetch audit logs");
        const data = await res.json();
        setLogs(data.items || []);
        setTotal(data.total || 0);
        setPage(data.page || 1);
        setPages(data.pages || 1);
      } catch (e) {
        showNotification(e.message || "Failed to fetch audit logs", "error");
      } finally {
        setLoading(false);
      }
    },
    [method, role, statusGroup, search, showNotification]
  );

  useEffect(() => {
    fetchLogs(1);
  }, [fetchLogs]);

  const handleRefresh = () => {
    fetchLogs(1);
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const query = new URLSearchParams({ page: "1", page_size: "10000" });
      if (search) query.append("search", search);
      if (method) query.append("method", method);
      if (role) query.append("role", role);
      if (statusGroup) query.append("status_group", statusGroup);
      const res = await apiFetch(`/tenant/audit-logs?${query.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch logs for export");
      const data = await res.json();
      const allItems = data.items || [];
      if (allItems.length === 0) {
        showNotification("No logs available to export", "warning");
        return;
      }
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      downloadFile(buildCsv(allItems), `audit-logs-${timestamp}.csv`, "text/csv;charset=utf-8");
      showNotification(`Exported ${allItems.length} log entries`, "success");
    } catch (e) {
      showNotification(e.message || "Export failed", "error");
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      key: "_sr",
      label: "S.No",
      render: (_val, _row, index) => (
        <span className="text-slate-600 font-medium text-xs">
          {(page - 1) * pageSize + index + 1}
        </span>
      ),
    },
    {
      key: "created_at",
      label: "When",
      render: (val) => (
        <span className="text-slate-800 whitespace-nowrap text-xs">{formatWhen(val)}</span>
      ),
    },
    {
      key: "actor_name",
      label: "Actor",
      render: (_val, row) => (
        <div className="min-w-[140px]">
          <div className="font-medium text-slate-900">{row.actor_name || "—"}</div>
          <div className="text-xs text-slate-600 break-all">{row.actor_email || ""}</div>
        </div>
      ),
    },
    {
      key: "actor_role",
      label: "Role",
      render: (val) => (
        <Badge variant={val === "tenant_admin" ? "primary" : "default"}>{roleLabel(val)}</Badge>
      ),
    },
    {
      key: "action",
      label: "Action",
      render: (val, row) => (
        <div className="min-w-[160px]">
          <div className="text-slate-900">{val}</div>
          <div className="text-[11px] text-slate-600 capitalize">{row.resource?.replace(/[-_]/g, " ")}</div>
        </div>
      ),
    },
    {
      key: "method",
      label: "Method",
      render: (val) => methodBadge(val),
    },
    {
      key: "status_code",
      label: "Status",
      render: (val) => statusBadge(val),
    },
    {
      key: "duration_ms",
      label: "Time",
      render: (val) => (
        <span className="text-xs text-slate-700 whitespace-nowrap">
          {val != null ? `${val} ms` : "—"}
        </span>
      ),
    },
  ];

  const selectClass =
    "rounded-lg border border-slate-200 bg-white text-sm text-slate-800 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500";

  return (
    <div className="space-y-6 animate-fade-in">
      <div data-tour-id="tour-audit-header" className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md flex-shrink-0">
            <ScrollText size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Audit Logs</h1>
            <p className="text-sm text-slate-600 flex items-center gap-1 mt-0.5">
              <Shield size={11} />
              Organisation-wide trail of tenant processes
            </p>
          </div>
        </div>
        <Button
          data-tour-id="tour-audit-refresh"
          variant="secondary"
          type="button"
          onClick={handleRefresh}
          disabled={loading}
          leftIcon={<RefreshCw size={14} className={loading ? "animate-spin" : ""} />}
        >
          Refresh Logs
        </Button>
      </div>

      <Card className="animate-fade-in">
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div data-tour-id="tour-audit-filters" className="flex flex-col sm:flex-row sm:items-center gap-2 w-full">
              <Input
                placeholder="Search actor, action, or module…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftElement={<Search size={14} className="text-slate-500" />}
                className="flex-1 max-w-md"
              />
              <select
                className={selectClass}
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              >
                <option value="">All methods</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
                <option value="GET">GET</option>
              </select>
              <select
                className={selectClass}
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="">All roles</option>
                <option value="tenant_admin">Tenant Admin</option>
                <option value="user">User</option>
              </select>
              <select
                className={selectClass}
                value={statusGroup}
                onChange={(e) => setStatusGroup(e.target.value)}
              >
                <option value="">All statuses</option>
                <option value="success">Success</option>
                <option value="client_error">Client error</option>
                <option value="server_error">Server error</option>
              </select>
              <Tooltip content={exporting ? "Exporting…" : "Export logs to Excel"}>
                <button
                  data-tour-id="tour-audit-export"
                  onClick={handleExportExcel}
                  disabled={exporting || loading}
                  className={`flex-shrink-0 w-9 h-9 rounded-lg border flex items-center justify-center transition-all ${
                    exporting || loading
                      ? "bg-emerald-50 text-emerald-300 border-emerald-100 cursor-not-allowed"
                      : "bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100 hover:text-emerald-700 hover:border-emerald-300 active:scale-95"
                  }`}
                >
                  {exporting ? (
                    <RefreshCw size={15} className="animate-spin" />
                  ) : (
                    <FileDown size={15} />
                  )}
                </button>
              </Tooltip>
            </div>
            {total > 0 && (
              <span className="text-xs text-slate-600 whitespace-nowrap">
                {total} total log{total !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-slate-600 text-sm">
            <RefreshCw size={14} className="animate-spin" /> Loading logs…
          </div>
        ) : (
          <>
            <div data-tour-id="tour-audit-table">
            <Table
              columns={columns}
              data={logs}
              emptyMessage="No audit log entries recorded yet. Tenant processes will appear here as users and admins work."
            />
            </div>
            <Pagination
              page={page}
              pages={pages}
              total={total}
              pageSize={pageSize}
              onPageChange={(p) => fetchLogs(p)}
              itemLabel="logs"
            />
          </>
        )}
      </Card>
    </div>
  );
}
